<template>
  <div class="signUpForm">
    <div class="center">
      <h1>{{ $t("resetPasswordSubmitPage.title") }}</h1>
      <p v-html="$t('resetPasswordSubmitPage.description')"></p>
      <form @submit.prevent="submit">
        <h2>{{ $t("resetPasswordSubmitPage.userLabel") }}: {{ username }}</h2>
        <div>
          <text-field :label="$t('resetPasswordSubmitPage.confirmCodeLabel')" v-model="code" :maxlength="6" />
          <text-field :label="$t('resetPasswordSubmitPage.newPasswordLabel')" type="password" v-model="newPassword" />
          <div class="attention">{{ $t("resetPasswordSubmitPage.passwordRequirement") }}</div>
          <button-default>{{ $t("resetPasswordSubmitPage.submitButton") }}</button-default>
        </div>
        <div v-if="message">{{ message }}</div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Auth } from "aws-amplify";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import TextField from "@/components/molecules/TextField.vue";
import { showNotifyDialog } from "@/lib/dialog";

interface Data {
  message: string;
  newPassword: string;
  code: string;
}

interface Methods {
  submit(): Promise<void>;
}

interface Computed {
  from: string | undefined;
}

interface Props {
  username: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { ButtonDefault, TextField },
  props: {
    username: String,
  },
  created() {
    if (this.from === "nolanovel") {
      document.title = this.$t("resetPasswordSubmitPage.pageTitle.nolaNovel") as string;
    } else if (this.from === "default") {
      document.title = this.$t("resetPasswordSubmitPage.pageTitle.default") as string;
    }
  },
  data() {
    return {
      message: "",
      newPassword: "",
      code: "",
    };
  },
  computed: {
    from() {
      return this.$route.query.from;
    },
  },
  methods: {
    async submit() {
      const { username, code, newPassword: password } = this;

      try {
        const res = await Auth.forgotPasswordSubmit(username, code, password);
        console.log(res);

        await showNotifyDialog({
          content:
            this.from === "default"
              ? this.$t("resetPasswordSubmitPage.successMessage.default")
              : this.$t("resetPasswordSubmitPage.successMessage.normal"),
        });
        if (this.from === "default") {
          window.location.href = "https://indent.co.jp/#product";
        } else if (this.from === "nolanovel") {
          window.location.href = `${process.env.VUE_APP_NOLANOVEL_WEB}/auth`;
        } else {
          this.$router.push({ name: "signIn" });
        }
      } catch (err) {
        const error = err as any;
        if (error.code === "CodeMismatchException") {
          this.message = this.$t("resetPasswordSubmitPage.validation.codeMismatch") as string;
        } else if (error.code === "UsernameExistsException") {
          this.message = this.$t("resetPasswordSubmitPage.validation.emailAlreadyExists") as string;
        } else if (error.code === "InvalidParameterException" || error.code === "InvalidPasswordException") {
          this.message = this.$t("resetPasswordSubmitPage.validation.invalidPassword") as string;
        } else if (error.code === "LimitExceededException") {
          this.message = this.$t("resetPasswordSubmitPage.validation.limitExceeded") as string;
        } else {
          this.message = `${this.$t("resetPasswordSubmitPage.validation.genericError")}: ${error.message}`;
        }
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";
h1 {
  font-size: var(--font-size-xl);
  color: var(--color-text-normal);
}
h2 {
  font-size: var(--font-size-md);
  color: var(--color-text-normal);
}
p {
  font-size: var(--font-size-xs);
  color: var(--color-text-normal);
}
.signUpForm {
  position: relative;
  width: 100%;
  color: var(--color-text-on-fixed-bg);
}

.center {
  margin: 30px 10%;
  padding: 30px 50px;
  background-color: var(--color-surface-base);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  text-align: center;

  @include mq(sp) {
    margin: 50px 15px;
    padding: 0;
  }
}
.center form {
  padding: 15px 0;
  margin: auto;
  font-size: 16.1px;
  width: 50%;

  @include mq(sp) {
    width: 100%;
  }
}
.signUpForm button {
  padding: 10px 80px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  font-size: var(--font-size-md);
  color: var(--color-text-on-fixed-bg);
}
.attention {
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}
</style>
